import type { OrgModuleDto } from "./modules.js";
import { z } from "zod";
import { buildingCodeSchema } from "./ids.js";
import { domainSchema, emailSchema, orgSlugSchema } from "./slug.js";

/**
 * Platformbeheer (SaaS): organisaties, abonnement en facturatie.
 * v1: handmatig factureren; prijs per actief (niet gearchiveerd) gebouw per maand, excl. btw.
 */
export const ORG_STATUSES = ["trial", "active", "read_only", "cancelled"] as const;
export type OrgStatus = (typeof ORG_STATUSES)[number];
export const ORG_STATUS_LABELS: Record<OrgStatus, string> = {
  trial: "Proefperiode",
  active: "Actief",
  read_only: "Alleen lezen",
  cancelled: "Opgezegd",
};
export const TRIAL_DAYS = 30;

export const INVOICE_STATUSES = ["open", "paid"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = { open: "Open", paid: "Betaald" };

/** Alleen lezen: opgezegd, handmatig op alleen-lezen gezet, of proefperiode verlopen. */
export function isReadOnly(status: OrgStatus, trialEndsAt: Date | string | null, now = new Date()): boolean {
  if (status === "read_only" || status === "cancelled") return true;
  if (status === "trial" && trialEndsAt) return new Date(trialEndsAt).getTime() < now.getTime();
  return false;
}

export interface Subscription {
  status: OrgStatus;
  trialEndsAt: string | null;
  readOnly: boolean;
}

/** Factuurgegevens van een klant (organisatie). */
export const billingDetailsSchema = z.object({
  company: z.string().trim().max(200).default(""),
  contact: z.string().trim().max(200).default(""),
  email: z.union([emailSchema, z.literal("")]).default(""),
  address: z.string().trim().max(300).default(""),
  postcode: z.string().trim().max(20).default(""),
  city: z.string().trim().max(100).default(""),
  kvk: z.string().trim().max(20).default(""),
  vat: z.string().trim().max(30).default(""),
  reference: z.string().trim().max(100).default(""),
  notes: z.string().trim().max(2000).default(""),
});
export type BillingDetails = z.infer<typeof billingDetailsSchema>;

const cents = z.number().int().min(0).max(100_000_000);

export const platformCreateOrgBodySchema = z.object({
  name: z.string().trim().min(1, "Naam is verplicht").max(120),
  slug: orgSlugSchema,
  ownerEmail: emailSchema,
  allowedDomains: z.array(domainSchema).max(20).default([]),
  /** 0 = direct actief. */
  trialDays: z.number().int().min(0).max(365).default(TRIAL_DAYS),
  pricePerBuildingCents: cents.default(0),
  /** Welkomstscherm, voorbeeldgebouw en checklist Aan de slag voor de eigenaar (issue #162). */
  onboarding: z.boolean().default(true),
});

export const platformUpdateOrgBodySchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  allowedDomains: z.array(domainSchema).max(20).optional(),
  status: z.enum(ORG_STATUSES).optional(),
  trialEndsAt: z.iso.date().nullable().optional(),
  pricePerBuildingCents: cents.optional(),
  billing: billingDetailsSchema.optional(),
  reportBrand: z.enum(["klantpunt", "pandwachters"]).optional(),
  elementCoding: z.boolean().optional(),
});

export const invoiceBodySchema = z
  .object({
    number: z.string().trim().max(50).default(""),
    periodStart: z.iso.date(),
    periodEnd: z.iso.date(),
    buildings: z.number().int().min(0).max(100_000),
    amountCents: cents,
    issuedOn: z.iso.date(),
    note: z.string().trim().max(500).default(""),
  })
  .refine((v) => v.periodEnd >= v.periodStart, { message: "Einde ligt voor het begin", path: ["periodEnd"] });

export const invoiceUpdateBodySchema = z.object({ status: z.enum(INVOICE_STATUSES) });
export const platformInviteOwnerBodySchema = z.object({ email: emailSchema });

export interface PlatformOrgRow {
  id: string;
  name: string;
  slug: string;
  status: OrgStatus;
  trialEndsAt: string | null;
  readOnly: boolean;
  buildings: number;
  members: number;
  pricePerBuildingCents: number;
  /** Gebouwen × prijs, per maand, excl. btw. */
  monthlyCents: number;
  openInvoiceCents: number;
  createdAt: string;
}

export interface InvoiceRow {
  id: string;
  number: string;
  periodStart: string;
  periodEnd: string;
  buildings: number;
  amountCents: number;
  status: InvoiceStatus;
  issuedOn: string;
  paidOn: string | null;
  note: string;
}

export interface PlatformOrgDetail extends PlatformOrgRow {
  allowedDomains: string[];
  reportBrand: "klantpunt" | "pandwachters";
  /** Volledige NLBE-SfB-tabel aan. */
  elementCoding: boolean;
  billing: BillingDetails;
  owners: { userId: string; email: string; name: string | null }[];
  ownerInvitations: { id: string; email: string; expiresAt: string }[];
  invoices: InvoiceRow[];
  /** Is de platformbeheerder zelf lid? */
  iAmMember: boolean;
  modules: OrgModuleDto[];
}

/** Abonnementsoverzicht voor de eigenaar van een organisatie. */
export interface OrgSubscriptionResponse extends Subscription {
  buildings: number;
  pricePerBuildingCents: number;
  monthlyCents: number;
}

export const formatEuro = (c: number) =>
  new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(c / 100);

// ── Gebouw overdragen naar een andere organisatie (docs/CLIENTS.md §14) ──

/** Gebouw in het overzicht van een organisatie in platformbeheer. */
export interface PlatformBuildingRow {
  id: string;
  code: string;
  name: string;
  address: string | null;
  clientName: string;
  archived: boolean;
}

export const buildingTransferBodySchema = z.object({
  toOrgId: z.uuid(),
  /** Code in de ontvangende organisatie; leeg = de huidige code. Nodig als die daar al bestaat. */
  code: buildingCodeSchema.nullish().transform((v) => v ?? null),
  /** Bevestiging: de huidige gebouwcode. Zonder bevestiging alleen een controle (er verandert niets). */
  confirm: z
    .string()
    .trim()
    .nullish()
    .transform((v) => v ?? null),
});
export type BuildingTransferBody = z.input<typeof buildingTransferBodySchema>;

/** Wat er meegaat en wat er verandert; bij de controle (zonder bevestiging) is er nog niets gedaan. */
export interface BuildingTransferResult {
  done: boolean;
  /** De code in de ontvangende organisatie. */
  code: string;
  /** De huidige code bestaat al in de ontvangende organisatie: kies een andere. */
  codeTaken: boolean;
  /** Assets (codes) met een QR-code die in de ontvangende organisatie al in gebruik is: dan kan het niet. */
  qrConflicts: string[];
  counts: {
    floors: number;
    assets: number;
    issues: number;
    tasks: number;
    obligations: number;
    budgetItems: number;
    assessments: number;
    meters: number;
    intake: number;
    files: number;
    contacts: number;
    documents: number;
  };
  /** Gebouwtoegang van medewerkers die vervalt. */
  removedMembers: number;
  /** Taken van medewerkers die geen lid zijn van de ontvangende organisatie: niet meer toegewezen. */
  unassignedTasks: number;
  /** Robaws-koppelingen die vervallen. */
  robawsLinks: number;
}
